/**
 * HScroll — reusable fixed-height horizontal-scroll carousel.
 *
 * §3.3 paging rules (hard-won prototype notes):
 *  1. No CSS `scroll-snap-type` — it rubber-bands when combined with
 *     programmatic scrollLeft assignment.
 *  2. No `scrollTo({ behavior })` / `scroll-behavior: smooth` —
 *     page via `el.scrollLeft = targetPage * el.clientWidth` only.
 *  3. Update `activePage` state *in the click handler*, not only from the
 *     scroll event — a programmatic scrollLeft write may not fire `scroll`.
 *  4. Right-edge peek of the next column is intentional; do not clip it.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './HScroll.css';

export interface HScrollProps {
  ariaLabel: string;
  className?: string;
  children: React.ReactNode;
}

export const HScroll: React.FC<HScrollProps> = ({ ariaLabel, className, children }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [pageCount, setPageCount] = useState(1);
  const [activePage, setActivePage] = useState(0);

  // ── Measurement ────────────────────────────────────────────────────────
  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const pages = Math.max(1, Math.ceil(el.scrollWidth / el.clientWidth));
    setPageCount(pages);
    // Recompute active page from current scrollLeft so dots stay in sync
    // after a native scroll (e.g. touch swipe).
    if (pages > 1 && el.clientWidth > 0) {
      const maxScroll = el.scrollWidth - el.clientWidth;
      const current = maxScroll > 0
        ? Math.round((el.scrollLeft / maxScroll) * (pages - 1))
        : 0;
      setActivePage(current);
    } else {
      setActivePage(0);
    }
  }, []);

  // Initial measurement + recompute on resize
  // useLayoutEffect runs synchronously after the DOM mutation; good for
  // reading layout geometry (scrollWidth / clientWidth).
  useLayoutEffect(() => {
    measure();
  }, [children, measure]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    // ResizeObserver: picks up dynamic width changes (e.g. parent resizes).
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }

    // Fallback: window resize
    window.addEventListener('resize', measure);

    // Sync active-dot with native/touch scroll
    el.addEventListener('scroll', measure, { passive: true });

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
      el.removeEventListener('scroll', measure);
    };
  }, [measure]);

  // ── Paging ─────────────────────────────────────────────────────────────
  const goToPage = useCallback((page: number) => {
    const el = trackRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(pageCount - 1, page));
    // §3.3: direct scrollLeft assignment — no scrollTo({ behavior }) / no CSS
    // scroll-behavior.
    el.scrollLeft = clamped * el.clientWidth;
    // §3.3: update state in the handler, not only from the scroll event.
    setActivePage(clamped);
  }, [pageCount]);

  const handlePrev = () => goToPage(activePage - 1);
  const handleNext = () => goToPage(activePage + 1);

  const showNav = pageCount > 1;

  return (
    <div className={['hscroll', className].filter(Boolean).join(' ')}>
      <div
        ref={trackRef}
        className="hscroll__track"
        aria-label={ariaLabel}
      >
        {children}
      </div>

      {showNav && (
        <div className="hscroll__nav" role="group" aria-label={`${ariaLabel} navigation`}>
          <button
            className="hscroll__arrow"
            aria-label="Previous"
            disabled={activePage === 0}
            onClick={handlePrev}
          >
            ‹
          </button>

          <div className="hscroll__dots" role="group" aria-label="Pages">
            {Array.from({ length: pageCount }, (_, i) => (
              <button
                key={i}
                className={['hscroll__dot', i === activePage ? 'hscroll__dot--active' : ''].filter(Boolean).join(' ')}
                aria-label={`Page ${i + 1}`}
                aria-current={i === activePage ? 'true' : undefined}
                onClick={() => goToPage(i)}
              />
            ))}
          </div>

          <button
            className="hscroll__arrow"
            aria-label="Next"
            disabled={activePage === pageCount - 1}
            onClick={handleNext}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default HScroll;
